import { Card, CardContent } from "@/components/ui/card";
import { AlertCircle, AlertTriangle, Clock, Timer } from "lucide-react";
import { DepletionMonthlySummary } from "@/hooks/useDepletionMonthly";

interface Props {
  summary: DepletionMonthlySummary;
  isLoading: boolean;
}

export function DepletionV2SummaryCards({ summary, isLoading }: Props) {
  if (isLoading) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {[1, 2, 3, 4].map((i) => (
          <Card key={i} className="animate-pulse">
            <CardContent className="p-6">
              <div className="h-20 bg-muted rounded" />
            </CardContent>
          </Card>
        ))}
      </div>
    );
  }

  const cards = [
    {
      title: "หมดคอร์ส",
      value: summary.total_depleted,
      icon: AlertCircle,
      iconColor: "text-destructive",
      bgColor: "bg-destructive/10",
      description: "นักเรียนที่หมดคอร์สและคาดว่าจะหมดคอร์สในช่วงเวลาที่เลือก",
    },
    {
      title: "ใกล้หมดคอร์ส",
      value: summary.total_nearly_depleted,
      icon: AlertTriangle,
      iconColor: "text-yellow-600",
      bgColor: "bg-yellow-50 dark:bg-yellow-950/40",
      description: "นักเรียนที่ใกล้หมดคอร์สและคาดว่าจะใกล้หมดคอร์สในช่วงเวลาที่เลือก (จะมีชั่วโมงเรียนน้อยกว่าหรือเท่ากับ 8)",
    },
    {
      title: "หมดคอร์สไปแล้ว",
      value: summary.total_hours_depleted,
      icon: Clock,
      iconColor: "text-destructive",
      bgColor: "bg-destructive/10",
      description: "ชั่วโมงคงเหลือในปัจจุบันน้อยกว่าหรือเท่ากับ 0 โดยนับเฉพาะนักเรียนในช่วงเวลาที่เลือก",
    },
    {
      title: "ใกล้หมดชั่วโมงแล้ว",
      value: summary.total_hours_nearly_depleted,
      icon: Timer,
      iconColor: "text-orange-600",
      bgColor: "bg-orange-50 dark:bg-orange-950/40",
      description: "ชั่วโมงคงเหลือในปัจจุบันน้อยกว่าหรือเท่ากับ 8 แต่มากกว่า 0 โดยนับเฉพาะนักเรียนในช่วงเวลาที่เลือก",
    },
  ];

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      {cards.map((card) => (
        <Card key={card.title} className={card.bgColor}>
          <CardContent className="p-6">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">{card.title}</p>
                <p className="text-3xl font-bold mt-1">{card.value} คน</p>
                <p className="text-xs text-muted-foreground mt-1">{card.description}</p>
              </div>
              <card.icon className={`h-8 w-8 ${card.iconColor}`} />
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}
